import { ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'
import { Settings2Icon, TriangleAlert } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import { hasNotesContent } from 'app/frontend/base/components/NotesEditor'
import RichTextContent from 'app/frontend/base/components/RichTextContent'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  EventActivityLinkedPlanEntry,
  EventActivityLinkedPlanData,
} from '../hooks/useEventActivityLinkedPlanData'
import { resolveLinkedPlanTimelineMaxTime } from '../hooks/resolveLinkedPlanEncounterAssignmentRows'
import { ignoreAllUnresolvedLinkedPlanIssues } from '../hooks/applyEventActivityPlanOverrides'
import {
  clearActorOverrides,
  clearCastTargetOverrides,
  getEncounterAssignmentAbilityHidden,
  getPlanEncounterAssignmentOverrides,
  EventPlanOverridesByPlanId,
} from './eventActivityPlanOverrides'
import EventActivityLinkedPlanTitleRow from './EventActivityLinkedPlanTitleRow'
import EventActivityVisualPlan from './EventActivityVisualPlan'
import ResolvedPlanPreviewContent from './ResolvedPlanPreviewContent'

type Props = {
  linkedPlans: EventActivityLinkedPlanEntry[]
  selectedParticipants: EventActivityLinkedPlanData['selectedParticipants']
  planOverrides: EventPlanOverridesByPlanId
  canEdit: boolean
  isSaving: boolean
  reorderEnabled?: boolean
  defaultExpanded?: boolean
  titlePrefix?: ReactNode
  onReplace: (planType: 'raid' | 'visual', planId: number) => void
  onUnlink: (planType: 'raid' | 'visual', planId: number) => void
  onMove?: (
    planType: 'raid' | 'visual',
    planId: number,
    direction: -1 | 1,
  ) => void
  onExportOverrides: (planId: number) => void
  onPlanOverridesChange: (nextPlanOverrides: EventPlanOverridesByPlanId) => void
}

const EventActivityLinkedPlanList = ({
  linkedPlans,
  selectedParticipants,
  planOverrides,
  canEdit,
  isSaving,
  reorderEnabled = false,
  defaultExpanded = true,
  titlePrefix,
  onReplace,
  onUnlink,
  onMove,
  onExportOverrides,
  onPlanOverridesChange,
}: Props) => {
  const { team } = useTeam()

  const hasPlanOverridesFor = (planId: number) => {
    const currentPlanOverrides = planOverrides[planId.toString()]
    if (!currentPlanOverrides) return false
    const actorEntries = currentPlanOverrides.actorOverrides?.actors ?? {}
    const abilityEntries =
      currentPlanOverrides.encounterAssignments?.abilities ?? {}
    return (
      Object.keys(actorEntries).length > 0 ||
      Object.keys(abilityEntries).length > 0
    )
  }

  return (
    <>
      {linkedPlans.map((linkedPlan, linkedPlanIndex) => {
        if (linkedPlan.type === 'visual') {
          const visualPlan = linkedPlan.data
          return (
            <EventActivityVisualPlan
              key={`visual-${visualPlan.id}`}
              visualPlan={visualPlan}
              selectedParticipants={selectedParticipants}
              planOverrides={planOverrides}
              canEdit={canEdit}
              titlePrefix={titlePrefix}
              defaultExpanded={defaultExpanded}
              onChange={() => onReplace('visual', visualPlan.id)}
              onUnlink={() => onUnlink('visual', visualPlan.id)}
              onMoveUp={
                reorderEnabled && onMove
                  ? () => onMove('visual', visualPlan.id, -1)
                  : undefined
              }
              onMoveDown={
                reorderEnabled && onMove
                  ? () => onMove('visual', visualPlan.id, 1)
                  : undefined
              }
              canMoveUp={linkedPlanIndex > 0}
              canMoveDown={linkedPlanIndex < linkedPlans.length - 1}
              onPlanOverridesChange={onPlanOverridesChange}
            />
          )
        }

        const raidPlanData = linkedPlan.data
        const linkedRaidPlan = raidPlanData.linkedRaidPlan
        const encounterAssignmentOverrides =
          getPlanEncounterAssignmentOverrides(planOverrides, linkedRaidPlan.id)
        const selectedAbilityRows = raidPlanData.linkedPlanAssignments.map(
          (entry) => ({
            abilityKey: entry.abilityKey,
            row: entry.row,
            ability: entry.ability,
            isHidden: getEncounterAssignmentAbilityHidden({
              encounterAssignments: encounterAssignmentOverrides,
              abilityKey: entry.row.abilityKey,
            }),
          }),
        )
        const sortedAbilityRows = [...selectedAbilityRows].sort(
          (left, right) => Number(left.isHidden) - Number(right.isHidden),
        )
        const cooldownMaxTime = resolveLinkedPlanTimelineMaxTime({
          raidPlan: linkedRaidPlan,
          timeline: linkedRaidPlan.timeline,
        })
        const planListPathPrefix =
          linkedRaidPlan.ownerType === 'User' ? '/profile' : team.path
        const editPath = `${planListPathPrefix}/events/plans/${linkedRaidPlan.id}`
        const isEmpty =
          raidPlanData.selectedRows.length === 0 &&
          raidPlanData.resolvedActors.length === 0

        return (
          <EventActivityLinkedPlanTitleRow
            key={`raid-${linkedRaidPlan.id}`}
            name={linkedRaidPlan.name}
            prefix={titlePrefix}
            editPath={editPath}
            canEdit={canEdit}
            defaultExpanded={defaultExpanded}
            onChange={() => onReplace('raid', linkedRaidPlan.id)}
            onUnlink={() => onUnlink('raid', linkedRaidPlan.id)}
            onMoveUp={
              reorderEnabled && onMove
                ? () => onMove('raid', linkedRaidPlan.id, -1)
                : undefined
            }
            onMoveDown={
              reorderEnabled && onMove
                ? () => onMove('raid', linkedRaidPlan.id, 1)
                : undefined
            }
            canMoveUp={linkedPlanIndex > 0}
            canMoveDown={linkedPlanIndex < linkedPlans.length - 1}
            issueCount={raidPlanData.linkedPlanIssueCount}
            extraActions={
              canEdit && hasPlanOverridesFor(linkedRaidPlan.id) ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant='outline'
                      size='xs'
                      aria-label='Plan override actions'
                    >
                      <Settings2Icon className='h-3.5 w-3.5' />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuItem
                      disabled={isSaving}
                      onSelect={(selectEvent) => {
                        selectEvent.preventDefault()
                        if (isSaving) return
                        onExportOverrides(linkedRaidPlan.id)
                      }}
                    >
                      Save changes to new plan
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null
            }
          >
            {hasNotesContent(linkedRaidPlan.message) ? (
              <div className='mb-4 flex flex-col gap-1'>
                <span className='text-sm text-muted-foreground'>Message</span>
                <Card>
                  <RichTextContent content={linkedRaidPlan.message ?? ''} />
                </Card>
              </div>
            ) : null}
            {canEdit && raidPlanData.linkedPlanIssueCount > 0 ? (
              <LinkedPlanIssuesAlert
                issueCount={raidPlanData.linkedPlanIssueCount}
                editPath={editPath}
                isSaving={isSaving}
                onIgnoreAll={() =>
                  onPlanOverridesChange(
                    ignoreAllUnresolvedLinkedPlanIssues({
                      planOverrides,
                      planId: linkedRaidPlan.id,
                      resolvedActors: raidPlanData.resolvedActors,
                      linkedPlanAssignments: raidPlanData.linkedPlanAssignments,
                      resolvedByScopeBoxKey:
                        raidPlanData.resolverResult.resolvedByScopeBoxKey,
                      resolvedByCastId:
                        raidPlanData.castTargetResolverResult.resolvedByCastId,
                      generalCasts:
                        linkedRaidPlan.timeline.general?.casts ?? [],
                    }),
                  )
                }
              />
            ) : null}
            {isEmpty ? (
              <p className='text-muted-foreground text-center mt-6 mb-8'>
                The linked raid plan for this encounter is empty
              </p>
            ) : null}
            <ResolvedPlanPreviewContent
              raidPlan={linkedRaidPlan}
              planListPathPrefix={planListPathPrefix}
              rosterMemberships={team.members ?? []}
              planOverrides={planOverrides}
              onPlanOverridesChange={onPlanOverridesChange}
              sortedAbilityRows={sortedAbilityRows}
              selectedRows={raidPlanData.selectedRows}
              phaseDividers={raidPlanData.phaseDividers}
              selectedParticipants={selectedParticipants}
              resolvedByScopeBoxKey={
                raidPlanData.resolverResult.resolvedByScopeBoxKey
              }
              resolvedActors={raidPlanData.resolvedActors}
              resolvedCastTargetsByCastId={
                raidPlanData.castTargetResolverResult.resolvedByCastId
              }
              cooldownMaxTime={cooldownMaxTime}
              canEdit={canEdit}
              isSaving={isSaving}
              cooldownHeaderActions={
                canEdit ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='outline'
                        size='xs'
                        data-testid='cooldown-settings-button'
                      >
                        <Settings2Icon className='h-3.5 w-3.5' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuItem
                        onSelect={() => {
                          const actorResetOverrides = clearActorOverrides({
                            planOverrides,
                            planId: linkedRaidPlan.id,
                          })
                          const nextPlanOverrides = clearCastTargetOverrides({
                            planOverrides: actorResetOverrides,
                            planId: linkedRaidPlan.id,
                          })
                          onPlanOverridesChange(nextPlanOverrides)
                        }}
                      >
                        Reset changes
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null
              }
            />
          </EventActivityLinkedPlanTitleRow>
        )
      })}
    </>
  )
}

type LinkedPlanIssuesAlertProps = {
  issueCount: number
  editPath: string
  isSaving: boolean
  onIgnoreAll: () => void
}

const LinkedPlanIssuesAlert = ({
  issueCount,
  editPath,
  isSaving,
  onIgnoreAll,
}: LinkedPlanIssuesAlertProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false)

  return (
    <>
      <Alert variant='warning' className='pb-2 mb-4'>
        <TriangleAlert className='h-4 w-4' />
        <div className='flex items-start justify-between gap-3'>
          <div>
            <AlertTitle>Issues to resolve: {issueCount}</AlertTitle>
            <AlertDescription>
              You can resolve issues directly in this event by clicking on them.
              You can also edit the{' '}
              <Link
                className='text-primary hover:text-primary/90'
                to={editPath}
              >
                base plan
              </Link>
              , which will affect all events that use it.
            </AlertDescription>
          </div>
          <Button
            variant='outline'
            size='xs'
            className='shrink-0'
            disabled={isSaving}
            onClick={() => setConfirmOpen(true)}
          >
            Ignore all
          </Button>
        </div>
      </Alert>
      <ConfirmDialog
        open={confirmOpen}
        title='Ignore all issues?'
        description='This will mark all unresolved cooldown actors, assignments, and cast targets as ignored for this event.'
        continueText='Ignore all'
        onCancel={() => setConfirmOpen(false)}
        onContinue={() => {
          onIgnoreAll()
          setConfirmOpen(false)
        }}
      />
    </>
  )
}

export default EventActivityLinkedPlanList
